import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { createLogger } from '../../utils/logger';

const logger = createLogger('resolveCartInstance');

export function getCartUserIdCandidates(checkoutData = {}, fallbackUserId) {
  const ids = [
    checkoutData.cartDocId,
    checkoutData.cart_doc_id,
    fallbackUserId,
    auth.currentUser?.uid,
    checkoutData.userId,
    checkoutData.user_id,
  ];
  const ignoredIds = new Set(['', 'unknown', 'simulated_user']);
  return Array.from(new Set(ids.filter((id) => typeof id === 'string' && !ignoredIds.has(id))));
}

/**
 * Load cart documents for payment callback resolution.
 * Falls back to full collection scan only when targeted reads find nothing (legacy behavior).
 */
export async function getAccessibleCartDocs({ checkoutData = {}, fallbackUserId } = {}) {
  const cartDocs = [];
  for (const userId of getCartUserIdCandidates(checkoutData, fallbackUserId)) {
    try {
      const cartSnap = await getDoc(doc(db, 'Carts', userId));
      if (cartSnap.exists()) {
        cartDocs.push(cartSnap);
      }
    } catch (error) {
      logger.warn('[resolveCartInstance] Could not read candidate cart document:', userId, error);
    }
  }

  if (cartDocs.length > 0) {
    return cartDocs;
  }

  logger.warn(
    '[resolveCartInstance] No targeted cart documents found for candidates:',
    getCartUserIdCandidates(checkoutData, fallbackUserId)
  );
  return [];
}

export function findCartInstanceInCartData(cartData, orderId) {
  if (!cartData || !orderId) return null;

  for (const supermarketId in cartData) {
    if (supermarketId === 'lastGlobalUpdate') continue;

    const supermarketCart = cartData[supermarketId];
    if (!supermarketCart || typeof supermarketCart !== 'object') continue;

    if (supermarketCart[orderId]) {
      return {
        supermarketId,
        supermarketCart,
        cartInstanceId: orderId,
        cartInstance: supermarketCart[orderId],
      };
    }

    for (const cartInstanceId of Object.keys(supermarketCart)) {
      const cartInstance = supermarketCart[cartInstanceId];
      if (cartInstance && cartInstance.orderid === orderId) {
        return {
          supermarketId,
          supermarketCart,
          cartInstanceId,
          cartInstance,
        };
      }
    }
  }

  return null;
}

/**
 * Locate a cart instance by stable cartInstanceId when WiPay order_id no longer
 * matches cart.orderid (e.g. checkout retry replaced the pending order id).
 */
export function findCartInstanceInCartDataByInstanceId(
  cartData,
  { supermarketId, cartInstanceId } = {}
) {
  if (!cartData || !cartInstanceId) return null;

  const supermarketIds = supermarketId
    ? [supermarketId]
    : Object.keys(cartData).filter((key) => key !== 'lastGlobalUpdate');

  for (const resolvedSupermarketId of supermarketIds) {
    const supermarketCart = cartData[resolvedSupermarketId];
    if (!supermarketCart || typeof supermarketCart !== 'object') continue;

    const cartInstance = supermarketCart[cartInstanceId];
    if (cartInstance) {
      return {
        supermarketId: resolvedSupermarketId,
        supermarketCart,
        cartInstanceId,
        cartInstance,
      };
    }
  }

  return null;
}

/**
 * Recover the checkout snapshot for a WiPay order_id. After a retry replaces
 * cart.orderid, the superseded snapshot is kept in pendingCheckoutSnapshots.
 */
export function resolveCheckoutSnapshotForOrder(cartInstance, orderId) {
  if (!cartInstance || !orderId) return null;

  const pendingSnapshot = cartInstance.pendingCheckoutSnapshots?.[orderId];
  if (pendingSnapshot && typeof pendingSnapshot === 'object') {
    return pendingSnapshot;
  }

  const currentSnapshot = cartInstance.checkoutSnapshot;
  if (!currentSnapshot || typeof currentSnapshot !== 'object') {
    return null;
  }

  if (currentSnapshot.wipayOrderId === orderId) {
    return currentSnapshot;
  }

  if (!currentSnapshot.wipayOrderId && cartInstance.orderid === orderId) {
    return currentSnapshot;
  }

  return null;
}

export function isCurrentCartOrderId(cartInstance, orderId) {
  if (!cartInstance || !orderId) return false;
  if (cartInstance.orderid === orderId) return true;
  if (cartInstance.orderid != null) return false;
  if (isSupersededCartOrderId(cartInstance, orderId)) return false;

  const snapshotOrderId = cartInstance.checkoutSnapshot?.wipayOrderId;
  return !snapshotOrderId || snapshotOrderId === orderId;
}

/**
 * WiPay order_id kept in pendingCheckoutSnapshots after checkout retry replaced cart.orderid.
 */
export function isSupersededCartOrderId(cartInstance, orderId) {
  if (!cartInstance || !orderId || cartInstance.orderid === orderId) {
    return false;
  }
  return Boolean(cartInstance.pendingCheckoutSnapshots?.[orderId]);
}

function isCartStillUnfulfilled(cartInstance) {
  return (
    cartInstance.status === CART_STATUS.ACTIVE ||
    cartInstance.status === CART_STATUS.PAYMENT_FAILED ||
    cartInstance.status === CART_STATUS.PAYMENT_CANCELLED_BY_USER
  );
}

/**
 * Whether a successful payment for orderId may update cart status or decrement inventory.
 * Blocks late callbacks for superseded attempts once the cart is already fulfilled.
 */
export function shouldFinalizeSuccessForCart(cartInstance, orderId) {
  if (!cartInstance || !orderId) return false;

  const alreadyFulfilled =
    cartInstance.status === CART_STATUS.AWAITING_PACKING ||
    StatusHelpers.isMoreAdvancedThanAwaitingPacking(cartInstance.status);
  if (alreadyFulfilled) return false;

  if (isCurrentCartOrderId(cartInstance, orderId)) return true;
  if (isSupersededCartOrderId(cartInstance, orderId) && isCartStillUnfulfilled(cartInstance)) {
    return true;
  }

  return false;
}

function cloneCartInstanceForUpdate(cartInstance) {
  const clone = { ...cartInstance };
  if (cartInstance.pendingCheckoutSnapshots) {
    clone.pendingCheckoutSnapshots = { ...cartInstance.pendingCheckoutSnapshots };
  }
  return clone;
}

/**
 * Atomically claim checkout fulfillment on a cart instance so concurrent WiPay
 * callbacks for superseded and current order ids cannot both decrement inventory.
 * @returns {Promise<{ claimed: boolean, reason?: string }>}
 */
export async function tryClaimCartCheckoutFulfillment({
  cartDocRef,
  supermarketId,
  cartInstanceId,
  orderId,
}) {
  if (!cartDocRef || !supermarketId || !cartInstanceId || !orderId) {
    return { claimed: false, reason: 'missing_args' };
  }

  try {
    return await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(cartDocRef);
      if (!snap.exists()) {
        return { claimed: false, reason: 'cart_not_found' };
      }

      const cartData = snap.data();
      const supermarketCart = cartData[supermarketId];
      const cartInstance = supermarketCart?.[cartInstanceId];
      if (!cartInstance) {
        return { claimed: false, reason: 'instance_not_found' };
      }

      if (!shouldFinalizeSuccessForCart(cartInstance, orderId)) {
        return { claimed: false, reason: 'not_eligible' };
      }

      const updatedInstance = cloneCartInstanceForUpdate(cartInstance);
      applySupersededOrderFulfillmentToCart(updatedInstance, orderId);

      if (!StatusHelpers.canUpdateToAwaitingPacking(updatedInstance.status)) {
        return { claimed: false, reason: 'already_fulfilled' };
      }

      updatedInstance.status = CART_STATUS.AWAITING_PACKING;
      updatedInstance.lastUpdatedAt = serverTimestamp();

      transaction.update(cartDocRef, {
        [`${supermarketId}.${cartInstanceId}`]: updatedInstance,
        lastGlobalUpdate: serverTimestamp(),
      });

      return { claimed: true };
    });
  } catch (error) {
    logger.error('[resolveCartInstance] Failed to claim cart checkout fulfillment:', error);
    return { claimed: false, reason: 'error' };
  }
}

const CLAIM_RACE_LOSS_REASONS = new Set(['not_eligible', 'already_fulfilled']);

function isClaimRaceLossReason(claimReason) {
  return CLAIM_RACE_LOSS_REASONS.has(claimReason);
}

/**
 * Whether transaction and inventory persistence should still run after a failed cart
 * claim. Shared by {@link shouldSkipFulfillmentAfterFailedClaim} (inverted) and
 * {@link shouldRunInventoryDecrementForOrder}.
 */
export function shouldPersistFulfillmentAfterFailedClaim(
  cartInstance,
  orderId,
  claimReason
) {
  if (!cartInstance || !orderId) {
    return false;
  }
  if (isCurrentCartOrderId(cartInstance, orderId)) {
    return true;
  }
  if (isClaimRaceLossReason(claimReason) && isSupersededCartOrderId(cartInstance, orderId)) {
    return true;
  }
  return false;
}

/**
 * When {@link tryClaimCartCheckoutFulfillment} loses a race, only skip transaction and
 * inventory persistence for superseded or unrelated order ids. Callers pass a pre-claim
 * cart snapshot that may still show `active` while the claim transaction already returned
 * {@code not_eligible} or {@code already_fulfilled} because a peer moved the cart to
 * awaiting_packing — use {@link isCurrentCartOrderId}, not snapshot status, for the
 * same order_id. {@code claimReason} is accepted for traceability at call sites.
 *
 * Late superseded callbacks after the cart already advanced are gated upstream by
 * {@link shouldFulfillSupersededOrderPayment} / {@link shouldFinalizeSuccessForCart}, so
 * the superseded + {@code claimReason} branch only applies to concurrent races with
 * stale snapshots (not late single callbacks without a claim attempt).
 */
export function shouldSkipFulfillmentAfterFailedClaim(
  cartInstance,
  orderId,
  claimReason
) {
  if (!cartInstance || !orderId) {
    return true;
  }
  return !shouldPersistFulfillmentAfterFailedClaim(cartInstance, orderId, claimReason);
}

/**
 * Whether inventory decrement should run for this payment callback.
 * Includes retry when cart claim left the instance awaiting_packing but a prior
 * decrement attempt failed (transaction still has inventoryDecremented !== true).
 * Mirrors {@link shouldSkipFulfillmentAfterFailedClaim} for same-order race losers
 * that hold a stale pre-claim snapshot ({@code claimReason} not_eligible/already_fulfilled).
 */
export function shouldRunInventoryDecrementForOrder({
  cartInstanceData,
  orderId,
  eligibleForFreshFulfillment,
  claimedCartFulfillment,
  hasResolvedCartDoc,
  claimReason,
}) {
  if (!cartInstanceData) {
    return true;
  }
  if (
    eligibleForFreshFulfillment &&
    (claimedCartFulfillment || !hasResolvedCartDoc)
  ) {
    return true;
  }
  if (
    !claimedCartFulfillment &&
    isClaimRaceLossReason(claimReason) &&
    shouldPersistFulfillmentAfterFailedClaim(cartInstanceData, orderId, claimReason)
  ) {
    return true;
  }
  return (
    isCurrentCartOrderId(cartInstanceData, orderId) &&
    cartInstanceData.status === CART_STATUS.AWAITING_PACKING
  );
}

/**
 * When a superseded WiPay order pays while checkout retry is in progress, restore the
 * paid attempt onto the live cart instance and drop the abandoned retry snapshot.
 * @returns {boolean} true when cart fields were updated from pendingCheckoutSnapshots
 */
export function applySupersededOrderFulfillmentToCart(cartInstance, orderId) {
  if (!cartInstance || !orderId || !isSupersededCartOrderId(cartInstance, orderId)) {
    return false;
  }

  const snapshot = cartInstance.pendingCheckoutSnapshots?.[orderId];
  if (!snapshot || typeof snapshot !== 'object') {
    return false;
  }

  cartInstance.orderid = orderId;
  cartInstance.checkoutSnapshot = snapshot;
  if (Array.isArray(snapshot.cartItems)) {
    cartInstance.products = snapshot.cartItems;
  }

  delete cartInstance.pendingCheckoutSnapshots[orderId];
  if (
    cartInstance.pendingCheckoutSnapshots &&
    Object.keys(cartInstance.pendingCheckoutSnapshots).length === 0
  ) {
    delete cartInstance.pendingCheckoutSnapshots;
  }

  return true;
}

/**
 * Whether a failed payment for orderId may reset live cart fields (status, orderid, snapshot).
 */
export function shouldResetCartForFailedOrder(cartInstance, orderId) {
  return isCurrentCartOrderId(cartInstance, orderId);
}

/**
 * Legacy carts stored on Users documents (targeted identity_id lookup only).
 * @returns {Promise<{ cartInstanceData: object, supermarketId: string, cartInstanceId?: string } | null>}
 */
export async function findLegacyCartInUsersByOrderId(
  orderId,
  { checkoutData = {}, fallbackUserId } = {}
) {
  if (!orderId) return null;

  for (const identityId of getCartUserIdCandidates(checkoutData, fallbackUserId)) {
    try {
      const usersQuery = query(
        collection(db, 'Users'),
        where('identity_id', '==', identityId),
        limit(1)
      );
      const usersSnapshot = await getDocs(usersQuery);
      if (usersSnapshot.empty) continue;

      const userData = usersSnapshot.docs[0].data();
      if (!userData?.cart) continue;

      for (const supermarketId in userData.cart) {
        if (supermarketId === 'lastGlobalUpdate') continue;
        const supermarketCart = userData.cart[supermarketId];
        if (!supermarketCart || typeof supermarketCart !== 'object') continue;

        if (supermarketCart[orderId]) {
          return {
            cartInstanceData: supermarketCart[orderId],
            supermarketId,
            cartInstanceId: orderId,
          };
        }

        for (const cartInstanceId of Object.keys(supermarketCart)) {
          const cartInstance = supermarketCart[cartInstanceId];
          if (cartInstance?.orderid === orderId) {
            return {
              cartInstanceData: cartInstance,
              supermarketId,
              cartInstanceId,
            };
          }
        }
      }
    } catch (error) {
      logger.warn(
        '[resolveCartInstance] Legacy Users cart lookup failed for identity:',
        identityId,
        error
      );
    }
  }

  return null;
}

export function findCartInstanceInCartDocs(cartDocs, orderId, checkoutData = null) {
  for (const cartDoc of cartDocs || []) {
    const cartData = cartDoc.data();
    const match = findCartInstanceInCartData(cartData, orderId);
    if (match) {
      return {
        cartDoc,
        cartData,
        ...match,
      };
    }
  }

  const cartInstanceId = checkoutData?.cartInstanceId;
  if (cartInstanceId) {
    const supermarketId = checkoutData.supermarketId || checkoutData.supermarket_id;
    for (const cartDoc of cartDocs || []) {
      const cartData = cartDoc.data();
      const match = findCartInstanceInCartDataByInstanceId(cartData, {
        supermarketId,
        cartInstanceId,
      });
      if (match) {
        return {
          cartDoc,
          cartData,
          ...match,
          resolvedByCartInstanceId: true,
        };
      }
    }
  }

  return null;
}
